import React, { useEffect, useMemo, useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { trackLandingEvent, captureUtmParams, getStoredUtmParams, getStoredLandingUrl } from './analytics';
import { getFeaturedProperty, resolveFeaturedEntry } from './featuredPropertyConfig';
import HoppyMascot from './HoppyMascot';

const HU_PHONE_REGEX = /^(\+36|06)[ -]?\d{1,2}[ -]?\d{3}[ -]?\d{3,4}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(value, { required, validation }) {
  const trimmed = (value || '').trim();
  if (required && !trimmed) return 'Ez a mező kötelező.';
  if (!trimmed) return null;
  if (validation === 'hu-phone' && !HU_PHONE_REGEX.test(trimmed)) {
    return 'Adj meg egy érvényes magyar telefonszámot (pl. 06301234567).';
  }
  if (validation === 'email' && !EMAIL_REGEX.test(trimmed)) {
    return 'Adj meg egy érvényes e-mail címet.';
  }
  return null;
}

export default function LandingWizard({ config }) {
  const featuredProperty = getFeaturedProperty();

  // Resolve entry point once on mount: visitors arriving via a featured
  // property campaign (e.g. utm_campaign=kiralyvolgy) land directly on the
  // featured offer screen.
  const [entry] = useState(() => {
    const utm = captureUtmParams();
    const featuredEntry = resolveFeaturedEntry(utm);
    return {
      screen: featuredEntry ? 'featured' : 'hero',
      landingEntry: featuredEntry ? featuredEntry.id : 'general_hopphomes',
      property: featuredEntry || featuredProperty,
    };
  });

  const property = entry.property;

  const [screen, setScreen] = useState(entry.screen);
  const [answers, setAnswers] = useState({});
  const [propertyInterest, setPropertyInterest] = useState(null);
  const [originalIntent, setOriginalIntent] = useState(null);
  const [funnelSwitches, setFunnelSwitches] = useState([]);
  const [contactValues, setContactValues] = useState({});
  const [contactErrors, setContactErrors] = useState({});
  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submittedFunnel, setSubmittedFunnel] = useState(null);

  const totalSteps = config.steps.length + 1;

  useEffect(() => {
    trackLandingEvent('landing_view', {
      campaign: config.campaign,
      source: config.source,
      landing_entry: entry.landingEntry,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (screen === 'featured') {
      trackLandingEvent('featured_offer_view', {
        campaign: config.campaign,
        featured_property: property?.featuredPropertyTag || null,
      });
    }
    if (screen === 'property_contact' || screen === config.steps.length) {
      trackLandingEvent('lead_form_view', {
        campaign: config.campaign,
        funnel: screen === 'property_contact' ? 'featured_property' : 'general',
      });
    }
    if (screen === 'thankyou') {
      trackLandingEvent('thank_you_view', { campaign: config.campaign });
    }
  }, [screen, config.campaign, config.steps.length, property]);

  const currentStep = typeof screen === 'number' ? config.steps[screen] : null;

  const progressLabel = useMemo(() => {
    if (typeof screen !== 'number') return null;
    return `${screen + 1}/${totalSteps}`;
  }, [screen, totalSteps]);

  function recordSwitch(from, to) {
    const sw = { from, to, at: new Date().toISOString() };
    setFunnelSwitches((prev) => [...prev, sw]);
    trackLandingEvent('funnel_switch', {
      campaign: config.campaign,
      from_funnel: from,
      to_funnel: to,
      featured_property: property?.featuredPropertyTag || null,
    });
  }

  function handleStart() {
    trackLandingEvent('start_questionnaire', { campaign: config.campaign });
    setScreen(property ? 'featured' : 0);
  }

  function handleFeaturedPrimary() {
    setOriginalIntent((prev) => prev || 'erdekel_a_haz');
    trackLandingEvent('featured_offer_interested', {
      campaign: config.campaign,
      featured_property: property?.featuredPropertyTag || null,
    });
    setScreen('property_interest');
  }

  function handleFeaturedSecondary() {
    setOriginalIntent((prev) => prev || 'sajat_hazat_tervezek');
    trackLandingEvent('featured_offer_skip', {
      campaign: config.campaign,
      featured_property: property?.featuredPropertyTag || null,
    });
    setScreen(0);
  }

  function handleSwitchToGeneral() {
    recordSwitch('featured_property', 'general');
    setScreen(0);
  }

  function handleSwitchToFeatured() {
    recordSwitch('general', 'featured_property');
    setScreen('featured');
  }

  function handleChoiceSelect(stepIndex, value) {
    const step = config.steps[stepIndex];
    setAnswers((prev) => ({ ...prev, [step.id]: value }));
    trackLandingEvent(step.gaEventName, { [step.gaParamKey]: value, campaign: config.campaign });

    if (stepIndex + 1 < config.steps.length) {
      setScreen(stepIndex + 1);
    } else {
      setScreen(config.steps.length);
    }
  }

  function handlePropertyInterestSelect(value) {
    setPropertyInterest(value);
    const step = property?.interestStep;
    if (step) {
      trackLandingEvent(step.gaEventName, {
        [step.gaParamKey]: value,
        campaign: config.campaign,
        featured_property: property.featuredPropertyTag,
      });
    }
    setScreen('property_contact');
  }

  function handleContactChange(name, value) {
    setContactValues((prev) => ({ ...prev, [name]: value }));
    if (contactErrors[name]) {
      setContactErrors((prev) => ({ ...prev, [name]: '' }));
    }
  }

  async function handleContactSubmit(e) {
    e.preventDefault();
    setSubmitError(null);

    const errors = {};
    for (const field of config.contactStep.fields) {
      const err = validateField(contactValues[field.name], field);
      if (err) errors[field.name] = err;
    }
    if (!consentChecked) {
      errors.__consent = 'Az adatkezelési hozzájárulás elfogadása kötelező.';
    }
    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return;
    }

    const isPropertyLead = screen === 'property_contact';
    const finalIntent = isPropertyLead ? 'erdekel_a_haz' : 'sajat_hazat_tervezek';

    setSubmitting(true);
    try {
      const leadData = {
        name: (contactValues.name || '').trim(),
        phone: (contactValues.phone || '').trim(),
        email: (contactValues.email || '').trim() || null,
        home_type: answers.home_type || null,
        size: answers.size || null,
        land_status: answers.land_status || null,
        timeline: answers.timeline || null,
        budget: answers.budget || null,
        source: config.source,
        campaign: config.campaign,
        landing_url: getStoredLandingUrl(),
        utm_params: getStoredUtmParams(),
        landing_entry: entry.landingEntry,
        original_intent: originalIntent,
        final_intent: finalIntent,
        featured_property: isPropertyLead && property ? property.featuredPropertyTag : null,
        featured_property_label: isPropertyLead && property ? property.notificationLabel : null,
        property_interest: isPropertyLead ? propertyInterest : null,
        funnel_switches: funnelSwitches.length > 0 ? funnelSwitches : null,
        formType: 'chatgpt_landing',
        status: 'new',
        createdAt: serverTimestamp(),
      };

      // Save to HOPPhomes leads collection
      await addDoc(collection(db, 'leads'), leadData);

      setSubmittedFunnel(isPropertyLead ? 'featured_property' : 'general');

      trackLandingEvent('lead_submit', {
        campaign: config.campaign,
        funnel: isPropertyLead ? 'featured_property' : 'general',
        landing_entry: entry.landingEntry,
        original_intent: originalIntent,
        final_intent: finalIntent,
        featured_property: leadData.featured_property,
        property_interest: leadData.property_interest,
        home_type: answers.home_type,
        size: answers.size,
        land_status: answers.land_status,
        timeline: answers.timeline,
        budget: answers.budget,
      });

      setScreen('thankyou');
    } catch (err) {
      console.error('[landing] lead submit error:', err);
      setSubmitError('Hiba történt a küldés közben. Kérlek próbáld újra, vagy hívj minket telefonon.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleMainSiteClick() {
    trackLandingEvent('main_site_click', { campaign: config.campaign });
  }

  const activeContactStep =
    screen === 'property_contact' && property
      ? { ...config.contactStep, ...property.contactStep }
      : config.contactStep;
  const activeThankYou =
    submittedFunnel === 'featured_property' && property
      ? { ...config.thankYou, ...property.thankYou }
      : config.thankYou;

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#0a0a0a]">
      {typeof screen === 'number' && (
        <ProgressBar current={screen + 1} total={totalSteps} color={config.brand.primaryColor} />
      )}

      {screen === 'hero' && <HeroScreen config={config} onStart={handleStart} />}

      {screen === 'featured' && property && (
        <FeaturedOfferScreen
          property={property}
          brand={config.brand}
          onPrimary={handleFeaturedPrimary}
          onSecondary={handleFeaturedSecondary}
        />
      )}

      {screen === 'property_interest' && property && (
        <PropertyInterestScreen
          property={property}
          brand={config.brand}
          selected={propertyInterest}
          onSelect={handlePropertyInterestSelect}
          onSwitchToGeneral={handleSwitchToGeneral}
        />
      )}

      {currentStep && typeof screen === 'number' && (
        <ChoiceScreen
          step={currentStep}
          stepIndex={screen}
          selected={answers[currentStep.id]}
          onSelect={handleChoiceSelect}
          brand={config.brand}
          switchToFeaturedLabel={property?.switchToFeaturedLabel}
          onSwitchToFeatured={property ? handleSwitchToFeatured : undefined}
        />
      )}

      {(screen === config.steps.length || screen === 'property_contact') && (
        <ContactScreen
          config={config}
          contactStep={activeContactStep}
          values={contactValues}
          errors={contactErrors}
          consentChecked={consentChecked}
          submitting={submitting}
          submitError={submitError}
          onChange={handleContactChange}
          onConsentChange={setConsentChecked}
          onSubmit={handleContactSubmit}
          switchLabel={
            screen === 'property_contact'
              ? property?.switchToGeneralLabel
              : property?.switchToFeaturedLabel
          }
          onSwitch={screen === 'property_contact' ? handleSwitchToGeneral : handleSwitchToFeatured}
        />
      )}

      {screen === 'thankyou' && (
        <ThankYouScreen
          config={config}
          thankYou={activeThankYou}
          onMainSiteClick={handleMainSiteClick}
        />
      )}
    </div>
  );
}

function ProgressBar({ current, total, color }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="w-full px-5 pt-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-semibold text-white/50">{current}/{total}</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function HeroScreen({ config, onStart }) {
  const { hero, brand } = config;

  return (
    <div className="flex-1 flex flex-col min-h-screen w-full bg-[#0a0a0a]">
      <div className="flex-1 flex flex-col px-5 py-6 max-w-lg mx-auto w-full">
        <div className="flex items-start justify-between mb-6">
          <div>
            <img src={brand.logoUrl} alt={brand.logoAlt} className="h-10 w-auto mb-1.5" />
            <p className="text-xs font-medium text-white/50">{hero.eyebrow}</p>
          </div>
        </div>

        <div className="mb-4">
          {hero.headlineTop && (
            <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3">
              {hero.headlineTop}
            </h1>
          )}
          <div className="flex flex-col gap-2">
            {hero.headlineLines.map((line, i) => (
              <div
                key={i}
                className="inline-block px-3 py-2 rounded-xl text-lg md:text-xl font-extrabold leading-tight w-fit"
                style={{
                  backgroundColor: brand.primaryColor,
                  color: '#fff',
                }}
              >
                {line.text}
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm text-white/60 mb-6 leading-relaxed">{hero.subheadline}</p>

        {hero.hoppySpeech && (
          <div className="w-full flex items-center gap-3 mb-6 bg-white/5 border border-white/10 rounded-2xl p-3">
            <div className="shrink-0">
              <HoppyMascot primaryColor={brand.primaryColor} size={56} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-semibold leading-snug">{hero.hoppySpeech}</p>
            </div>
          </div>
        )}

        <button
          onClick={onStart}
          className="w-full py-4 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-[0.98] mb-6"
          style={{ backgroundColor: brand.primaryColor }}
        >
          {hero.ctaLabel}
        </button>

        {hero.trustBadges?.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {hero.trustBadges.map((badge, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-1">
                <span className="text-xl">{badge.icon}</span>
                <span className="text-[10px] font-semibold text-white/60 leading-tight">{badge.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturedOfferScreen({ property, brand, onPrimary, onSecondary }) {
  const [openAccordion, setOpenAccordion] = useState(null);

  return (
    <div className="flex-1 flex flex-col px-5 py-6 max-w-lg mx-auto w-full animate-fade-in">
      <p className="text-[11px] font-bold tracking-widest mb-3" style={{ color: brand.primaryColor }}>
        {property.eyebrow}
      </p>

      {property.video ? (
        <video
          src={property.video}
          className="w-full h-auto max-h-[70vh] rounded-2xl object-contain mb-5 bg-white/5"
          autoPlay
          muted
          loop
          playsInline
        />
      ) : property.image ? (
        <img
          src={property.image}
          alt={property.imageAlt}
          className="w-full aspect-[4/3] rounded-2xl object-cover mb-5"
        />
      ) : (
        <div className="w-full aspect-[4/3] rounded-2xl mb-5 bg-white/[0.04] border border-dashed border-white/20 flex flex-col items-center justify-center gap-2">
          <span className="text-3xl">🏠</span>
          <span className="text-xs font-semibold text-white/50">{property.placeholderLabel}</span>
        </div>
      )}

      <h2 className="text-2xl font-extrabold text-white leading-tight mb-1">{property.title}</h2>
      <p className="text-sm text-white/60 mb-2">{property.specs}</p>
      <p className="text-3xl font-extrabold text-white mb-4">{property.price}</p>

      {property.highlights?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {property.highlights.map((h, i) => (
            <span
              key={i}
              className="text-[11px] font-semibold text-white/80 bg-white/[0.06] border border-white/10 rounded-full px-3 py-1.5"
            >
              {h}
            </span>
          ))}
        </div>
      )}

      <button
        onClick={onPrimary}
        className="w-full py-4 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-[0.98] mb-3"
        style={{ backgroundColor: brand.primaryColor }}
      >
        {property.primaryCta}
      </button>
      <button
        onClick={onSecondary}
        className="w-full py-3.5 rounded-2xl font-semibold text-sm border border-white/15 bg-white/[0.04] text-white/80 hover:bg-white/[0.08] transition-all active:scale-[0.98] mb-6"
      >
        {property.secondaryCta}
      </button>

      {property.accordions?.length > 0 && (
        <div className="flex flex-col gap-2">
          {property.accordions.map((acc) => {
            const isOpen = openAccordion === acc.id;
            return (
              <div key={acc.id} className="border border-white/10 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenAccordion(isOpen ? null : acc.id)}
                  className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                >
                  <span className="text-xs font-bold text-white/80 tracking-wide">{acc.title}</span>
                  <span
                    className="text-white/50 text-lg leading-none transition-transform"
                    style={{ transform: isOpen ? 'rotate(45deg)' : 'none' }}
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 text-sm text-white/60 leading-relaxed">
                    {acc.content ? (
                      Array.isArray(acc.content) ? (
                        <ul className="list-disc pl-4 flex flex-col gap-1">
                          {acc.content.map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                        </ul>
                      ) : (
                        <p>{acc.content}</p>
                      )
                    ) : (
                      <p className="italic text-white/40">A részletes tartalom hamarosan érkezik.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PropertyInterestScreen({ property, brand, selected, onSelect, onSwitchToGeneral }) {
  const step = property.interestStep;

  return (
    <div className="flex-1 flex flex-col px-5 py-8 max-w-lg mx-auto w-full animate-fade-in">
      <p className="text-[11px] font-bold tracking-widest mb-2" style={{ color: brand.primaryColor }}>
        {property.title}
      </p>
      <h2 className="text-2xl font-bold text-white mb-6">{step.question}</h2>

      <div className="flex flex-col gap-3">
        {step.options.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(opt.value)}
              className={`w-full py-4 px-4 rounded-2xl text-left font-semibold text-sm transition-all border ${
                isSelected
                  ? 'border-transparent text-white'
                  : 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]'
              }`}
              style={isSelected ? { backgroundColor: brand.primaryColor } : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {onSwitchToGeneral && (
        <button
          onClick={onSwitchToGeneral}
          className="mt-8 text-center text-xs text-white/50 underline underline-offset-2 hover:text-white/80 transition-colors"
        >
          {property.switchToGeneralLabel}
        </button>
      )}
    </div>
  );
}

function ChoiceScreen({ step, stepIndex, selected, onSelect, brand, switchToFeaturedLabel, onSwitchToFeatured }) {
  return (
    <div className="flex-1 flex flex-col px-5 py-8 max-w-lg mx-auto w-full animate-fade-in">
      {step.hoppySpeech && (
        <div className="flex items-center gap-3 mb-6 bg-white/5 border border-white/10 rounded-2xl p-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0"
            style={{ backgroundColor: brand.primaryColor }}
          >
            <HoppyMascot primaryColor={brand.primaryColor} size={40} />
          </div>
          <p className="text-sm text-white font-medium">{step.hoppySpeech}</p>
        </div>
      )}

      <h2 className="text-2xl font-bold text-white mb-6">{step.question}</h2>

      <div className="flex flex-col gap-3">
        {step.options.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => onSelect(stepIndex, opt.value)}
              className={`w-full py-4 px-4 rounded-2xl text-left font-semibold text-sm transition-all border ${
                isSelected
                  ? 'border-transparent text-white'
                  : 'border-white/10 bg-white/[0.04] text-white hover:bg-white/[0.08]'
              }`}
              style={isSelected ? { backgroundColor: brand.primaryColor } : undefined}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {onSwitchToFeatured && switchToFeaturedLabel && (
        <button
          onClick={onSwitchToFeatured}
          className="mt-8 text-center text-xs text-white/50 underline underline-offset-2 hover:text-white/80 transition-colors"
        >
          {switchToFeaturedLabel}
        </button>
      )}
    </div>
  );
}

function ContactScreen({
  config,
  contactStep,
  values,
  errors,
  consentChecked,
  submitting,
  submitError,
  onChange,
  onConsentChange,
  onSubmit,
  switchLabel,
  onSwitch,
}) {
  const { brand } = config;

  return (
    <div className="flex-1 flex flex-col px-5 py-8 max-w-lg mx-auto w-full animate-fade-in">
      <h2 className="text-2xl font-bold text-white mb-2">{contactStep.headline}</h2>
      <p className="text-sm text-white/60 mb-6 leading-relaxed">{contactStep.subheadline}</p>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {contactStep.fields.map((field) => (
          <div key={field.name}>
            <label className="block text-xs font-semibold text-white/60 mb-1.5">
              {field.label}
              {field.required && ' *'}
            </label>
            <input
              type={field.type}
              value={values[field.name] || ''}
              onChange={(e) => onChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              className={`w-full px-4 py-3 rounded-xl bg-white/[0.06] border text-white placeholder-white/30 text-sm focus:outline-none ${
                errors[field.name] ? 'border-red-500' : 'border-white/10 focus:border-white/30'
              }`}
            />
            {errors[field.name] && <p className="text-red-400 text-xs mt-1">{errors[field.name]}</p>}
          </div>
        ))}

        <label className="flex items-start gap-2.5 mt-1 cursor-pointer">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => onConsentChange(e.target.checked)}
            className="mt-0.5 w-4 h-4 shrink-0 accent-[#e31937]"
          />
          <span className="text-xs text-white/60 leading-relaxed">
            {contactStep.consentText}{' '}
            <a
              href={contactStep.consentLinkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-white/80"
            >
              {contactStep.consentLinkLabel}
            </a>
            {contactStep.consentTextAfter ? ` ${contactStep.consentTextAfter}` : ''}
          </span>
        </label>
        {errors.__consent && <p className="text-red-400 text-xs -mt-2">{errors.__consent}</p>}

        {submitError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-xs">
            {submitError}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-4 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-[0.98] disabled:opacity-60 mt-1"
          style={{ backgroundColor: brand.primaryColor }}
        >
          {submitting ? 'Küldés...' : contactStep.ctaLabel}
        </button>

        {onSwitch && switchLabel && (
          <button
            type="button"
            onClick={onSwitch}
            className="text-center text-xs text-white/50 underline underline-offset-2 hover:text-white/80 transition-colors"
          >
            {switchLabel}
          </button>
        )}

        <p className="text-center text-[11px] text-white/40">{contactStep.footnote}</p>
      </form>
    </div>
  );
}

function ThankYouScreen({ config, thankYou, onMainSiteClick }) {
  const { brand } = config;
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-5 py-8 max-w-lg mx-auto w-full text-center animate-fade-in">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
        style={{ backgroundColor: brand.primaryColor }}
      >
        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h2 className="text-2xl font-extrabold text-white mb-3">{thankYou.headline}</h2>
      <p className="text-sm text-white/60 mb-8 leading-relaxed">{thankYou.subtext}</p>
      <a
        href={thankYou.ctaHref}
        onClick={onMainSiteClick}
        className="w-full py-4 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-transform active:scale-[0.98]"
        style={{ backgroundColor: brand.primaryColor }}
      >
        {thankYou.ctaLabel}
      </a>
    </div>
  );
}
